import React, {
  useRef, useState, useEffect, useMemo,
} from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import getConfig from 'next/config';
import NavigatorLink from 'views/common/components/Logical/NavigatorLink';
import { useDispatch, useSelector } from 'react-redux';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import axios, { API } from 'lib/config/axios';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import PATHS from 'lib/navigation/paths';
import ScrollTo from 'lib/utils/ScrollTo';
import geGAFromCategory from 'lib/utils/geGAFromCategory';
import { CountriesAutocomplete } from 'views/common/components';
import { StyledBox, StyledInternationalPhone } from './FormSection.styles';
import { useContent } from 'views/rebranded-module/common/context/useContent';
interface FormSectionProps {
  leadCategories?: Array<any>;
  leadTypes?: Array<any>;
  contactInfo?: Record<string, any>;
}

const initialState = {
  category_id: '',
  lead_type_id: '',
  reservation_number: '',
  comments: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  full_phone: '',
  country_code: '',
  'g-recaptcha-response': '',
  referring_page: null,
  user_id: null,
  privacyPolicy: false,
};
const {
  publicRuntimeConfig: { envVars },
} = getConfig();

const FormSection: React.FC<FormSectionProps> = (props) => {
  const { leadCategories = [], leadTypes = [], contactInfo = {} } = props;

  const { t } = useTranslation('contact_us_V2_page');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const dispatch = useDispatch();
  const { appState, agentLocationInfo } = useSelector(
    (state: any) => state.globalData,
  );
  const userId = appState?.current_user ? appState.current_user.id : null;
  const appNavigator = useAppNavigator();
  const handleErrorResponse = useErrorsHandler();
  const { lead_category_identifier: leadCategoryIdentifier } = appNavigator.query;
  const content = useContent('FormSection');
  const leadCategoryOrder = [
    'Motorcycle Rentals',
    'Self-Guided Tours',
    'Guided Tours',
    'Membership',
    'Custom Trips',
    'Off-Road Tours',
  ];

  const filteredLeadCategories = useMemo(() => {
    const filtered = leadCategories
      .filter((cat) => leadCategoryOrder.includes(cat?.name))
      .sort(
        (a, b) => leadCategoryOrder.indexOf(a.name) - leadCategoryOrder.indexOf(b.name),
      );
    return filtered.length ? filtered : leadCategories;
  }, [leadCategories]);

  const initialContactUsFormData = useMemo(
    () => ({
      ...initialState,
      ...contactInfo,
      lead_type_id:
        contactInfo?.lead_type_id || (leadTypes && leadTypes[1]?.id) || '',
      category_id:
        leadCategories.find((cat) => cat.identifier == leadCategoryIdentifier)?.id
        || leadCategories?.[0]?.id
        || '',
      country_code: agentLocationInfo?.code || 'US',
    }),
    [contactInfo, leadCategories, leadCategoryIdentifier, leadTypes, agentLocationInfo?.code],
  );

  const [errorMsgs, setErrorMsgs] = useState<Record<string, any>>({});
  const [contactUsFormData, setContactUsFormData] = useState(initialContactUsFormData);
  const [hasUserSelectedCountry, setHasUserSelectedCountry] = useState(false);

  const handleInputChange = (e: any) => {
    setContactUsFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRecaptchChange = (token: string | null) => {
    setContactUsFormData({
      ...contactUsFormData,
      'g-recaptcha-response': token,
    });
  };

  const handlePrivacyPolicyCheck = (e: any) => setContactUsFormData({
    ...contactUsFormData,
    privacyPolicy: e.target.checked,
  });

  const sendGoogleAnalyticsEvent = (gaInfo: any) => {
    const { eventName, eventLabel } = gaInfo;

    if (typeof window !== 'undefined') {
      window.gtag('event', eventName, {
        event_category: 'Lead',
        event_action: 'lead_capture',
        event_label: eventLabel,
      });
    }
  };

  const handleSendButtonClick = async (e: any) => {
    e.preventDefault();
    // Client-side validation for required fields before calling the API
    const clientErrors: Record<string, string> = {};

    if (!contactUsFormData.country_code) {
      clientErrors.country_code = t('contact_us_V2_page:country_required');
    }
    if (!contactUsFormData.category_id) {
      clientErrors.category_id = t('contact_us_V2_page:product_required');
    }
    if (!contactUsFormData.lead_type_id) {
      clientErrors.lead_type_id = t('contact_us_V2_page:reason_required');
    }

    if (Object.keys(clientErrors).length) {
      // replace previous required-field errors to avoid stale messages
      setErrorMsgs((prev) => ({
        ...Object.fromEntries(
          Object.entries(prev).filter(
            ([key]) => !['country_code', 'category_id', 'lead_type_id'].includes(key),
          ),
        ),
        ...clientErrors,
      }));
      return;
    }

    dispatch(setLoading(true));
    if (!contactUsFormData.privacyPolicy) {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: content.erReadTermsAndConditions,
        }),
      );
      dispatch(setLoading(false));
      return;
    }
    try {
      const { 'g-recaptcha-response': recaptchaResponse, ...rest } = contactUsFormData;
      const { data } = await axios.post(API.contactUs, {
        authenticity_token: appState?.authenticity_token,
        'g-recaptcha-response': recaptchaResponse,
        contact_info: {
          ...rest,
          referring_page: typeof window !== 'undefined' ? window.location.href : null,
          user_id: userId,
          phone: rest.full_phone,
        },
      });
      sendGoogleAnalyticsEvent(geGAFromCategory(rest.category_id));
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: content.successMsg.replace('%{reference_number}', data.reference_number),
          duration: 10000,
        }),
      );
      setContactUsFormData(initialContactUsFormData); // Reset dialog
      dispatch(setLoading(false));
    } catch (error: any) {
      let message = '';
      let isExpected = false;
      if (error.response?.status && error.response.data) {
        const errorData = error.response.data;
        switch (error.response.status) {
          case 406:
            if (errorData.errors) {
              setErrorMsgs({
                ...errorData.errors.contact_info,
              });
              if (errorData.errors.contact_info.base) {
                [message] = errorData.errors.contact_info.base;
              } else message = null;
              isExpected = true;
            }
            dispatch(setLoading(false));
            break;
          case 403:
            message = errorData.message;
            isExpected = true;
            dispatch(setLoading(false));
            break;
          default:
            break;
        }
      }
      if (message) {
        dispatch(
          setSnackbar({
            open: true,
            severity: 'error',
            message,
          }),
        );
      }
      if (!isExpected) {
        handleErrorResponse(error);
      }
      dispatch(setLoading(false));
    } finally {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  useEffect(() => {
    if (leadCategoryIdentifier) {
      ScrollTo('contact-us-form');
    }
  }, [leadCategoryIdentifier]);

  useEffect(() => {
    if (
      !hasUserSelectedCountry
      && agentLocationInfo?.code
      && agentLocationInfo.code !== contactUsFormData.country_code
    ) {
      setContactUsFormData((prevData) => ({
        ...prevData,
        country_code: agentLocationInfo.code,
      }));
    }
  }, [agentLocationInfo?.code, contactUsFormData.country_code, hasUserSelectedCountry]);

  const membershipId = useMemo(
    () => leadCategories.find(
      (leadCategory) => leadCategory.identifier === 'membership',
    )?.id,
    [leadCategories],
  );

  const filterLeadTypes = useMemo(() => {
    if (contactUsFormData?.category_id != membershipId) {
      return leadTypes.filter((leadType) => !leadType.membership);
    }
    return leadTypes;
  }, [contactUsFormData?.category_id, leadTypes, membershipId]);

  return (
    <StyledBox id="contact-us-form">
      <Box className="FSWrapper">
        <Typography component="p" variant="h6" className="FSHeader">
          {content.yourDetails}
        </Typography>
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                required
                variant="outlined"
                fullWidth
                label={content.firstName}
                className="FSFormInput"
                value={contactUsFormData.first_name}
                name="first_name"
                onChange={handleInputChange}
                error={Boolean(errorMsgs.first_name)}
                helperText={errorMsgs.first_name}
                autoComplete="name"
                disabled={Boolean(contactInfo?.first_name)}
              />
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                required
                variant="outlined"
                fullWidth
                label={content.lastName}
                className="FSFormInput"
                value={contactUsFormData.last_name}
                name="last_name"
                onChange={handleInputChange}
                error={Boolean(errorMsgs.last_name)}
                helperText={errorMsgs.last_name}
                disabled={Boolean(contactInfo?.last_name)}
              />
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                required
                variant="outlined"
                fullWidth
                label={content.email}
                className="FSFormInput"
                autoComplete="email"
                type="email"
                name="email"
                value={contactUsFormData.email}
                onChange={handleInputChange}
                error={Boolean(errorMsgs.email)}
                helperText={errorMsgs.email}
                disabled={Boolean(contactInfo?.email)}
              />
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <Box className="FSFormInput">
                <StyledInternationalPhone
                  name="phone"
                  value={contactUsFormData.phone || ''}
                  disabled={false}
                  error={Boolean(errorMsgs.phone)}
                  helperText={errorMsgs.phone}
                  handleChange={(phoneData) => {
                    setContactUsFormData((prevData) => ({
                      ...prevData,
                      phone: phoneData.value || '',
                      full_phone: phoneData.number || '',
                      country_code: phoneData.countryCode || '',
                    }));
                  }}
                  handleBlur={(phoneData) => {
                    setContactUsFormData((prevData) => ({
                      ...prevData,
                      phone: phoneData.value || '',
                      full_phone: phoneData.number || '',
                      country_code: phoneData.countryCode || '',
                    }));
                  }}
                  skipValidation={false}
                />
                {errorMsgs.phone && (
                  <Typography variant="caption" className="FSPhoneError">
                    {errorMsgs.phone}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item lg={12} md={12} sm={12}>
              <CountriesAutocomplete
                label={content.country}
                className="FSFormInput"
                variant="outlined"
                value={contactUsFormData.country_code}
                error={Boolean(errorMsgs.country_code)}
                errorText={errorMsgs.country_code}
                disabled={false}
                required={true}
                onChange={(_, code) => {
                  setHasUserSelectedCountry(true);
                  handleInputChange({
                    target: {
                      name: 'country_code',
                      value: code,
                    },
                  });
                }}
              />
            </Grid>
          </Grid>
        </Box>
        <Box className="FSDivider" />
        <Typography component="p" variant="h6" className="FSDetailsTitle">
          {content.message}
        </Typography>
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                required
                select
                label={content.product}
                variant="outlined"
                fullWidth
                className="FSFormInput"
                name="category_id"
                value={contactUsFormData.category_id}
                onChange={handleInputChange}
                error={Boolean(errorMsgs.category_id)}
                helperText={errorMsgs.category_id}>
                {leadCategories ? (
                  leadCategories.map((option) => (
                    <MenuItem key={`product ${option.id}`} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem key="product-0" value="" />
                )}
              </TextField>
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                required
                select
                label={content.reason}
                variant="outlined"
                fullWidth
                className="FSFormInput"
                name="lead_type_id"
                value={contactUsFormData.lead_type_id}
                onChange={handleInputChange}
                error={Boolean(errorMsgs.lead_type_id)}
                helperText={errorMsgs.lead_type_id}>
                {filterLeadTypes.length ? (
                  filterLeadTypes.map((option) => (
                    <MenuItem key={`reason ${option.id}`} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem key="reason-0" value="" />
                )}
              </TextField>
            </Grid>
            <Grid item lg={12} md={12} sm={12}>
              <TextField
                label={content.reservationNumber}
                variant="outlined"
                fullWidth
                className="FSFormInput"
                name="reservation_number"
                value={contactUsFormData.reservation_number}
                onChange={handleInputChange}
                error={Boolean(errorMsgs.reservation_number)}
                helperText={errorMsgs.reservation_number}
                autoComplete="one-time-code"
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12}>
              <TextField
                multiline
                fullWidth
                rows={3}
                variant="outlined"
                className="FSFormInput"
                name="comments"
                value={contactUsFormData.comments}
                onChange={handleInputChange}
                error={Boolean(errorMsgs.comments)}
                helperText={errorMsgs.comments}
                placeholder={content.messagePlaceholder}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            className="FSCheckBox"
            control={(
              <Checkbox
                className="FSCheckIcon"
                name="privacyPolicy"
                checked={contactUsFormData.privacyPolicy}
                onChange={handlePrivacyPolicyCheck}
              />
            )}
            label={(
              <Typography component="p" variant="body1" className="FSReadAgreed">
                {content.readAgreed}
                &nbsp;
                <br className="showMOBILE" />
                <NavigatorLink
                  href={PATHS.privacyPolicy()}
                  color="primary"
                  className="FSPrivacyLink"
                  target="_blank">
                  <Typography component="span" variant="body1">
                    {content.erPrivacyPolicy}
                  </Typography>
                </NavigatorLink>
              </Typography>
            )}
          />
          {envVars.RECAPTCHA_SITE_KEY && (
            <Box mt={2} mb={3}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={envVars.RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchChange}
              />
            </Box>
          )}
          <Box className="FSSendBtnContainer">
            <Button
              className="FSSendBtn"
              size="large"
              variant="contained"
              onClick={handleSendButtonClick}>
              {content.sendButton}
            </Button>
          </Box>
        </Box>
      </Box>
    </StyledBox>
  );
};

export { FormSection };


